import React, { useState, useEffect, useRef } from 'react';
import { doc, getDoc, updateDoc, setDoc, collection, query, where, getDocs, orderBy, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { QRCodeSVG } from 'qrcode.react';
import {
    Share2, Copy, ExternalLink, QrCode, Download,
    Users, TrendingUp, CheckCircle, Gift, MessageSquare,
    Building2, Calendar, Eye, Search, X, Link2
} from 'lucide-react';

function SellerReferralLink({ userInfo }) {
    const [referralCode, setReferralCode] = useState('');
    const [referralStats, setReferralStats] = useState({
        views: 0,
        signups: 0
    });
    const [referredClinics, setReferredClinics] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showQR, setShowQR] = useState(false);
    const [copied, setCopied] = useState(false);
    const qrRef = useRef(null);

    // 기공소 선택 관련 상태
    const [labs, setLabs] = useState([]);
    const [selectedLab, setSelectedLab] = useState(null);
    const [labSearchQuery, setLabSearchQuery] = useState('');
    const [showLabSearch, setShowLabSearch] = useState(false);

    // 기공소가 선택되면 URL에 lab 파라미터 추가
    const referralUrl = referralCode
        ? selectedLab
            ? `${window.location.origin}/invite/${referralCode}?lab=${selectedLab.id}`
            : `${window.location.origin}/invite/${referralCode}`
        : '';

    // 홍보 메시지 템플릿 (기공소 정보 포함)
    const promotionMessage = selectedLab
        ? `[DentalCluster 특별 초대]

안녕하세요! ${userInfo?.businessName || '파트너사'}입니다.

DentalCluster 가입 시 기공료 10% 할인 혜택을 받으실 수 있습니다!

🏭 추천 기공소: ${selectedLab.businessName}
👉 가입 링크: ${referralUrl}

✅ 기공료 10% 할인
✅ ${selectedLab.businessName} 기공소와 자동 연결
✅ 편리한 주문 관리

지금 바로 가입하세요!`
        : `[DentalCluster 특별 초대]

안녕하세요! ${userInfo?.businessName || '파트너사'}입니다.

DentalCluster 가입 시 기공료 10% 할인 혜택을 받으실 수 있습니다!

👉 가입 링크: ${referralUrl}

✅ 기공료 10% 할인
✅ 재료 구매 할인 (예정)
✅ 검증된 기공소 연결
✅ 편리한 주문 관리

지금 바로 가입하세요!`;

    useEffect(() => {
        if (userInfo?.uid) {
            loadReferralData();
            loadLabs();
        }
    }, [userInfo]);

    // 기공소 목록 로드
    const loadLabs = async () => {
        try {
            const labsQuery = query(
                collection(db, 'users'),
                where('businessType', '==', 'lab')
            );
            const snapshot = await getDocs(labsQuery);
            const labsList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setLabs(labsList);
        } catch (error) {
            console.error('기공소 목록 로드 실패:', error);
        }
    };

    // 필터링된 기공소 목록
    const filteredLabs = labs.filter(lab =>
        lab.businessName?.toLowerCase().includes(labSearchQuery.toLowerCase()) ||
        lab.name?.toLowerCase().includes(labSearchQuery.toLowerCase())
    );

    // 추천 코드 생성 함수
    const generateReferralCode = () => {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        let code = '';
        for (let i = 0; i < 6; i++) {
            code += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return code;
    };

    const loadReferralData = async () => {
        try {
            setLoading(true);

            // 재료상 정보 로드
            const userRef = doc(db, 'users', userInfo.uid);
            const userDoc = await getDoc(userRef);

            if (userDoc.exists()) {
                const data = userDoc.data();

                // 승인된 재료상인데 referralCode가 없으면 자동 생성
                if (data.sellerStatus === 'approved' && !data.referralCode) {
                    const newCode = generateReferralCode();
                    await updateDoc(userRef, {
                        referralCode: newCode,
                        referralCount: 0,
                        referralViews: 0
                    });

                    // 공개 referralCodes 컬렉션에도 저장 (비로그인 사용자 조회용)
                    await setDoc(doc(db, 'referralCodes', newCode), {
                        sellerId: userInfo.uid,
                        sellerName: data.businessName || data.name || '파트너사',
                        createdAt: serverTimestamp()
                    });

                    setReferralCode(newCode);
                    setReferralStats({ views: 0, signups: 0 });
                } else {
                    setReferralCode(data.referralCode || '');
                    setReferralStats({
                        views: data.referralViews || 0,
                        signups: data.referralCount || 0
                    });

                    // 기존 코드가 referralCodes 컬렉션에 없으면 생성
                    if (data.referralCode) {
                        const codeRef = doc(db, 'referralCodes', data.referralCode);
                        const codeDoc = await getDoc(codeRef);
                        if (!codeDoc.exists()) {
                            await setDoc(codeRef, {
                                sellerId: userInfo.uid,
                                sellerName: data.businessName || data.name || '파트너사',
                                createdAt: serverTimestamp()
                            });
                            console.log('기존 코드 referralCodes 컬렉션에 등록 완료');
                        }
                    }
                }
            }

            // 추천으로 가입한 치과 목록 로드
            const clinicsQuery = query(
                collection(db, 'users'),
                where('referredBy', '==', userInfo.uid),
                orderBy('createdAt', 'desc')
            );

            try {
                const clinicsSnapshot = await getDocs(clinicsQuery);
                const clinics = clinicsSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setReferredClinics(clinics);
            } catch (e) {
                // 인덱스가 없을 경우 기본 쿼리
                const simpleQuery = query(
                    collection(db, 'users'),
                    where('referredBy', '==', userInfo.uid)
                );
                const clinicsSnapshot = await getDocs(simpleQuery);
                const clinics = clinicsSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setReferredClinics(clinics);
            }

        } catch (error) {
            console.error('추천 데이터 로딩 실패:', error);
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const downloadQRCode = () => {
        if (!qrRef.current) return;

        const svg = qrRef.current.querySelector('svg');
        if (!svg) return;

        const svgData = new XMLSerializer().serializeToString(svg);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();

        img.onload = () => {
            canvas.width = 300;
            canvas.height = 300;
            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0, 300, 300);

            const link = document.createElement('a');
            link.download = `dentalcluster-referral-${referralCode}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
        };

        img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return '-';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString('ko-KR', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <div style={styles.loadingContainer}>
                <div style={styles.spinner}></div>
                <p>데이터를 불러오는 중...</p>
            </div>
        );
    }

    if (!referralCode) {
        const isApproved = userInfo?.sellerStatus === 'approved';
        return (
            <div style={styles.container}>
                <div style={styles.emptyState}>
                    <Share2 size={64} color="#94a3b8" />
                    <h2 style={styles.emptyTitle}>
                        {isApproved ? '추천 코드 생성 중...' : '판매자 승인이 필요합니다'}
                    </h2>
                    <p style={styles.emptyText}>
                        {isApproved
                            ? '잠시만 기다려주세요. 페이지를 새로고침 해보세요.'
                            : '판매자 승인을 받으시면 추천 코드가 자동으로 생성됩니다.'}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            {/* 헤더 */}
            <div style={styles.header}>
                <h1 style={styles.title}>
                    <Share2 size={28} />
                    치과 홍보
                </h1>
                <p style={styles.subtitle}>
                    추천 링크를 공유하여 치과를 DentalCluster에 초대하세요
                </p>
            </div>

            {/* 통계 카드 */}
            <div style={styles.statsGrid}>
                <div style={styles.statCard}>
                    <div style={styles.statIcon}>
                        <Eye size={24} />
                    </div>
                    <div style={styles.statValue}>{referralStats.views}</div>
                    <div style={styles.statLabel}>링크 조회</div>
                </div>
                <div style={styles.statCard}>
                    <div style={{...styles.statIcon, backgroundColor: '#d1fae5'}}>
                        <Users size={24} color="#059669" />
                    </div>
                    <div style={{...styles.statValue, color: '#059669'}}>{referralStats.signups}</div>
                    <div style={styles.statLabel}>가입 치과</div>
                </div>
                <div style={styles.statCard}>
                    <div style={{...styles.statIcon, backgroundColor: '#fef3c7'}}>
                        <Gift size={24} color="#f59e0b" />
                    </div>
                    <div style={{...styles.statValue, color: '#f59e0b'}}>10%</div>
                    <div style={styles.statLabel}>할인 혜택</div>
                </div>
            </div>

            {/* 추천 링크 섹션 */}
            <div style={styles.section}>
                <h2 style={styles.sectionTitle}>
                    <Gift size={22} />
                    내 추천 링크
                </h2>

                <div style={styles.linkBox}>
                    <div style={styles.linkUrl}>{referralUrl}</div>
                    <div style={styles.linkActions}>
                        <button
                            style={{...styles.actionButton, backgroundColor: copied ? '#10b981' : '#6366f1'}}
                            onClick={() => copyToClipboard(referralUrl)}
                        >
                            {copied ? <CheckCircle size={18} /> : <Copy size={18} />}
                            {copied ? '복사됨!' : '링크 복사'}
                        </button>
                        <button
                            style={{...styles.actionButton, backgroundColor: '#64748b'}}
                            onClick={() => window.open(referralUrl, '_blank')}
                        >
                            <ExternalLink size={18} />
                            미리보기
                        </button>
                        <button
                            style={{...styles.actionButton, backgroundColor: '#f59e0b'}}
                            onClick={() => setShowQR(!showQR)}
                        >
                            <QrCode size={18} />
                            {showQR ? 'QR 숨기기' : 'QR 보기'}
                        </button>
                    </div>
                </div>

                {/* QR 코드 */}
                {showQR && (
                    <div style={styles.qrSection}>
                        <div ref={qrRef} style={styles.qrWrapper}>
                            <QRCodeSVG
                                value={referralUrl}
                                size={180}
                                level="H"
                                includeMargin={true}
                            />
                        </div>
                        <button style={styles.downloadButton} onClick={downloadQRCode}>
                            <Download size={18} />
                            QR코드 다운로드
                        </button>
                        <p style={styles.qrTip}>
                            명함이나 브로슈어에 QR코드를 삽입하세요!
                        </p>
                    </div>
                )}
            </div>

            {/* 기공소 선택 섹션 */}
            <div style={styles.section}>
                <h2 style={styles.sectionTitle}>
                    <Link2 size={22} />
                    기공소 연결 설정
                </h2>
                <p style={styles.sectionDesc}>
                    기공소를 선택하면 치과 가입 시 해당 기공소와 자동으로 연결됩니다.
                </p>

                {selectedLab ? (
                    <div style={styles.selectedLabBox}>
                        <div style={styles.selectedLabInfo}>
                            <Building2 size={24} color="#6366f1" />
                            <div>
                                <div style={styles.selectedLabName}>{selectedLab.businessName}</div>
                                <div style={styles.selectedLabMeta}>선택된 기공소</div>
                            </div>
                        </div>
                        <button
                            style={styles.changeLabButton}
                            onClick={() => {
                                setSelectedLab(null);
                                setShowLabSearch(true);
                            }}
                        >
                            변경
                        </button>
                    </div>
                ) : (
                    <button
                        style={styles.selectLabButton}
                        onClick={() => setShowLabSearch(true)}
                    >
                        <Building2 size={20} />
                        기공소 선택하기
                    </button>
                )}

                {/* 기공소 검색 모달 */}
                {showLabSearch && (
                    <div style={styles.labSearchModal}>
                        <div style={styles.labSearchHeader}>
                            <div style={styles.labSearchInputBox}>
                                <Search size={18} color="#94a3b8" />
                                <input
                                    type="text"
                                    value={labSearchQuery}
                                    onChange={(e) => setLabSearchQuery(e.target.value)}
                                    placeholder="기공소 이름 검색..."
                                    style={styles.labSearchInput}
                                    autoFocus
                                />
                            </div>
                            <button
                                style={styles.labSearchClose}
                                onClick={() => {
                                    setShowLabSearch(false);
                                    setLabSearchQuery('');
                                }}
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <div style={styles.labList}>
                            {filteredLabs.length === 0 ? (
                                <div style={styles.noLabsFound}>
                                    검색 결과가 없습니다.
                                </div>
                            ) : (
                                filteredLabs.map(lab => (
                                    <button
                                        key={lab.id}
                                        style={styles.labItem}
                                        onClick={() => {
                                            setSelectedLab(lab);
                                            setShowLabSearch(false);
                                            setLabSearchQuery('');
                                        }}
                                    >
                                        <Building2 size={20} color="#64748b" />
                                        <div style={styles.labItemInfo}>
                                            <div style={styles.labItemName}>
                                                {lab.businessName || lab.name}
                                            </div>
                                            {lab.address && (
                                                <div style={styles.labItemAddress}>
                                                    {lab.address}
                                                </div>
                                            )}
                                        </div>
                                    </button>
                                ))
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* 홍보 메시지 템플릿 */}
            <div style={styles.section}>
                <h2 style={styles.sectionTitle}>
                    <MessageSquare size={22} />
                    홍보 문구 템플릿
                </h2>
                <div style={styles.messageBox}>
                    <pre style={styles.messageText}>{promotionMessage}</pre>
                    <button
                        style={styles.copyMessageButton}
                        onClick={() => copyToClipboard(promotionMessage)}
                    >
                        <Copy size={18} />
                        문구 복사하기
                    </button>
                </div>
                <p style={styles.messageTip}>
                    카카오톡이나 문자로 위 메시지를 복사하여 거래처 치과에 전송하세요!
                </p>
            </div>

            {/* 추천 가입 치과 목록 */}
            <div style={styles.section}>
                <h2 style={styles.sectionTitle}>
                    <Building2 size={22} />
                    추천 가입 치과 ({referredClinics.length}개)
                </h2>

                {referredClinics.length === 0 ? (
                    <div style={styles.emptyList}>
                        <Users size={48} color="#cbd5e1" />
                        <p>아직 추천으로 가입한 치과가 없습니다.</p>
                        <p style={styles.emptyListSub}>링크를 공유하여 치과를 초대해보세요!</p>
                    </div>
                ) : (
                    <div style={styles.clinicList}>
                        {referredClinics.map((clinic, index) => (
                            <div key={clinic.id} style={styles.clinicCard}>
                                <div style={styles.clinicInfo}>
                                    <div style={styles.clinicName}>
                                        {clinic.businessName || clinic.name || '치과'}
                                    </div>
                                    <div style={styles.clinicMeta}>
                                        <Calendar size={14} />
                                        {formatDate(clinic.createdAt)}
                                    </div>
                                </div>
                                <div style={styles.discountBadge}>
                                    10% 할인
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
}

const styles = {
    container: {
        padding: '24px',
        maxWidth: '900px',
        margin: '0 auto',
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '400px',
        color: '#64748b',
    },
    spinner: {
        width: '40px',
        height: '40px',
        border: '3px solid #e2e8f0',
        borderTop: '3px solid #6366f1',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        marginBottom: '16px',
    },
    header: {
        marginBottom: '32px',
    },
    title: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        fontSize: '28px',
        fontWeight: '700',
        color: '#1e293b',
        marginBottom: '8px',
    },
    subtitle: {
        fontSize: '16px',
        color: '#64748b',
    },
    statsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '16px',
        marginBottom: '32px',
    },
    statCard: {
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '24px',
        textAlign: 'center',
        boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
    },
    statIcon: {
        width: '56px',
        height: '56px',
        borderRadius: '14px',
        backgroundColor: '#eef2ff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 12px',
        color: '#6366f1',
    },
    statValue: {
        fontSize: '32px',
        fontWeight: '700',
        color: '#6366f1',
    },
    statLabel: {
        fontSize: '14px',
        color: '#64748b',
        marginTop: '4px',
    },
    section: {
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '28px',
        marginBottom: '24px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
    },
    sectionTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        fontSize: '20px',
        fontWeight: '700',
        color: '#1e293b',
        marginBottom: '20px',
    },
    linkBox: {
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        padding: '20px',
        border: '2px solid #e2e8f0',
    },
    linkUrl: {
        fontSize: '14px',
        color: '#6366f1',
        fontWeight: '500',
        wordBreak: 'break-all',
        marginBottom: '16px',
        padding: '12px',
        backgroundColor: 'white',
        borderRadius: '8px',
    },
    linkActions: {
        display: 'flex',
        gap: '10px',
        flexWrap: 'wrap',
    },
    actionButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '12px 20px',
        fontSize: '14px',
        fontWeight: '600',
        color: 'white',
        border: 'none',
        borderRadius: '10px',
        cursor: 'pointer',
        transition: 'opacity 0.2s',
    },
    qrSection: {
        marginTop: '24px',
        padding: '24px',
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        textAlign: 'center',
    },
    qrWrapper: {
        display: 'inline-block',
        padding: '16px',
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    },
    downloadButton: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        marginTop: '16px',
        padding: '12px 24px',
        fontSize: '14px',
        fontWeight: '600',
        color: 'white',
        backgroundColor: '#10b981',
        border: 'none',
        borderRadius: '10px',
        cursor: 'pointer',
    },
    qrTip: {
        marginTop: '12px',
        fontSize: '13px',
        color: '#64748b',
    },
    messageBox: {
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        padding: '20px',
        border: '2px solid #e2e8f0',
    },
    messageText: {
        fontSize: '14px',
        color: '#475569',
        lineHeight: 1.7,
        whiteSpace: 'pre-wrap',
        margin: '0 0 16px 0',
        fontFamily: 'inherit',
    },
    copyMessageButton: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        padding: '12px 24px',
        fontSize: '14px',
        fontWeight: '600',
        color: 'white',
        backgroundColor: '#6366f1',
        border: 'none',
        borderRadius: '10px',
        cursor: 'pointer',
    },
    messageTip: {
        marginTop: '16px',
        fontSize: '13px',
        color: '#64748b',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
    },
    clinicList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
    },
    clinicCard: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 20px',
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
    },
    clinicInfo: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    clinicName: {
        fontSize: '16px',
        fontWeight: '600',
        color: '#1e293b',
    },
    clinicMeta: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '13px',
        color: '#64748b',
    },
    discountBadge: {
        padding: '6px 14px',
        fontSize: '13px',
        fontWeight: '600',
        color: '#059669',
        backgroundColor: '#d1fae5',
        borderRadius: '20px',
    },
    emptyList: {
        padding: '48px 24px',
        textAlign: 'center',
        color: '#94a3b8',
    },
    emptyListSub: {
        fontSize: '14px',
        marginTop: '8px',
    },
    emptyState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '80px 24px',
        textAlign: 'center',
    },
    emptyTitle: {
        fontSize: '24px',
        fontWeight: '700',
        color: '#1e293b',
        margin: '24px 0 12px',
    },
    emptyText: {
        fontSize: '16px',
        color: '#64748b',
    },
    sectionDesc: {
        fontSize: '14px',
        color: '#64748b',
        marginBottom: '20px',
    },
    selectLabButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        width: '100%',
        padding: '16px 20px',
        fontSize: '15px',
        fontWeight: '600',
        color: '#6366f1',
        backgroundColor: '#eef2ff',
        border: '2px dashed #c7d2fe',
        borderRadius: '12px',
        cursor: 'pointer',
        justifyContent: 'center',
    },
    selectedLabBox: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 20px',
        backgroundColor: '#f0fdf4',
        border: '2px solid #86efac',
        borderRadius: '12px',
    },
    selectedLabInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
    },
    selectedLabName: {
        fontSize: '16px',
        fontWeight: '600',
        color: '#1e293b',
    },
    selectedLabMeta: {
        fontSize: '13px',
        color: '#059669',
    },
    changeLabButton: {
        padding: '8px 16px',
        fontSize: '14px',
        fontWeight: '600',
        color: '#6366f1',
        backgroundColor: 'white',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        cursor: 'pointer',
    },
    labSearchModal: {
        marginTop: '16px',
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
        overflow: 'hidden',
    },
    labSearchHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px 16px',
        borderBottom: '1px solid #e2e8f0',
        backgroundColor: 'white',
    },
    labSearchInputBox: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flex: 1,
        padding: '8px 12px',
        backgroundColor: '#f1f5f9',
        borderRadius: '8px',
    },
    labSearchInput: {
        flex: 1,
        border: 'none',
        outline: 'none',
        backgroundColor: 'transparent',
        fontSize: '14px',
    },
    labSearchClose: {
        padding: '8px',
        backgroundColor: 'transparent',
        border: 'none',
        cursor: 'pointer',
        color: '#94a3b8',
    },
    labList: {
        maxHeight: '300px',
        overflowY: 'auto',
    },
    labItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        width: '100%',
        padding: '14px 16px',
        backgroundColor: 'white',
        border: 'none',
        borderBottom: '1px solid #f1f5f9',
        cursor: 'pointer',
        textAlign: 'left',
    },
    labItemInfo: {
        flex: 1,
    },
    labItemName: {
        fontSize: '15px',
        fontWeight: '600',
        color: '#1e293b',
    },
    labItemAddress: {
        fontSize: '13px',
        color: '#64748b',
        marginTop: '2px',
    },
    noLabsFound: {
        padding: '32px',
        textAlign: 'center',
        color: '#94a3b8',
        fontSize: '14px',
    },
};

export default SellerReferralLink;
